import "./RemainingPagesResponsive.css";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../api";
import AdminLayout from "../components/AdminLayout";
import {
  Home, Search, Calendar, Download, MoreVertical, ChevronLeft, ChevronRight, ChevronDown, Pencil, Eye,
} from "lucide-react";
import { money, fmtDate, ADMIN_QUOTE_STATUS, quoteExpiry, pageWindow, downloadCsv } from "../quoteUtils";

function statusInfo(r) {
  const s = r.quoteStatus;
  const meta = ADMIN_QUOTE_STATUS[s] || ADMIN_QUOTE_STATUS.pending;
  if (s === "approved") return { ...meta, sub: `Approved ${fmtDate(r.updatedAt)}` };
  if (s === "declined") return { ...meta, sub: `Declined ${fmtDate(r.updatedAt)}` };
  if (s === "revision_requested") return { ...meta, sub: "Customer wants changes" };
  if (s === "draft") return { ...meta, sub: r.quotedAt ? `Previous version sent ${fmtDate(r.quotedAt)}` : "Not sent yet" };
  if (s === "sent") {
    const left = quoteExpiry(r.quotedAt);
    if (left != null && left <= 0) return { ...meta, label: "Expired", cls: "bg-orange-100 text-orange-700", sub: `Sent ${fmtDate(r.quotedAt)}` };
    return { ...meta, label: "Awaiting response", sub: left != null ? `Expires in ${left} day${left === 1 ? "" : "s"}` : "" };
  }
  return meta;
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "sent", label: "Awaiting response" },
  { key: "revision_requested", label: "Revision requested" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
];

const PAGE_SIZE = 10;

export default function QuotesList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [page, setPage] = useState(1);
  const menuRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/admin/quotes");
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setError("Couldn't load quotes.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // only requests that actually have a quote (backend returns "pending" ones too)
  const quoted = useMemo(() => rows.filter((r) => r.quoteStatus && r.quoteStatus !== "pending" && r.quoteId), [rows]);

  const counts = useMemo(() => {
    const c = { all: quoted.length };
    quoted.forEach((r) => { c[r.quoteStatus] = (c[r.quoteStatus] || 0) + 1; });
    return c;
  }, [quoted]);

  const filtered = useMemo(() => {
    let list = statusFilter === "all" ? quoted : quoted.filter((r) => r.quoteStatus === statusFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [r.quoteId, r.requestId, r.customerName, r.customerContact, r.partRequired, r.partNumber, r.vin, r.rego]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      list = list.filter((r) => new Date(r.quotedAt || r.updatedAt) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((r) => new Date(r.quotedAt || r.updatedAt) <= to);
    }
    return list;
  }, [quoted, statusFilter, query, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [query, dateFrom, dateTo, statusFilter]);

  const exportCsv = () =>
    downloadCsv(
      "quotes.csv",
      ["Quote ID", "Request ID", "Customer", "Contact", "Part Required", "Quote Date", "Amount (AUD)", "Status"],
      filtered.map((r) => [
        r.quoteId, r.requestId, r.customerName, r.customerContact, r.partRequired,
        fmtDate(r.quotedAt || r.updatedAt), r.quotePricing?.total ?? "", statusInfo(r).label,
      ])
    );

  return (
    <AdminLayout>
      <main className="p-3 sm:p-6 agp-rem-page">
        <div className="mb-6">
          <h1 className="text-3xl font-extrabold text-slate-900">Quotes</h1>
          <div className="flex items-center gap-1.5 text-sm text-slate-400 mt-1">
            <Home size={14} />
            <Link to="/admin/dashboard" className="hover:text-slate-600">Dashboard</Link>
            <span>›</span>
            <span className="text-slate-500">Quotes</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm">
          {/* status tabs */}
          <div className="flex gap-2 px-5 pt-5 flex-wrap">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setStatusFilter(f.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold ${statusFilter === f.key ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {f.label} <span className="opacity-60">{counts[f.key] || 0}</span>
              </button>
            ))}
          </div>

          {/* toolbar */}
          <div className="flex items-center gap-3 p-5 border-b border-gray-100 flex-wrap">
            <div className="flex-1 flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 max-w-md min-w-[220px]">
              <Search size={16} className="text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by quote ID, request ID, customer, part..."
                className="flex-1 text-sm focus:outline-none placeholder:text-slate-400"
              />
            </div>

            <div className="relative ml-auto">
              <button onClick={() => setShowDateMenu((s) => !s)}
                className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm">
                <Calendar size={15} className="text-slate-400" />
                <span className="text-left">
                  <span className="block text-[10px] text-slate-400 font-semibold uppercase leading-none mb-0.5">Date Range</span>
                  <span className="font-semibold text-slate-700">
                    {dateFrom || dateTo ? `${dateFrom || "…"} – ${dateTo || "…"}` : "All time"}
                  </span>
                </span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {showDateMenu && (
                <div className="absolute right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-lg p-4 z-10 w-64 space-y-3">
                  <label className="block text-xs text-slate-400 font-semibold">From
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-slate-700" />
                  </label>
                  <label className="block text-xs text-slate-400 font-semibold">To
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                      className="w-full mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-slate-700" />
                  </label>
                  <div className="flex justify-between">
                    <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
                    <button onClick={() => setShowDateMenu(false)} className="text-xs font-semibold text-blue-600">Done</button>
                  </div>
                </div>
              )}
            </div>

            <button onClick={exportCsv}
              className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Download size={15} /> Export
            </button>
          </div>

          {/* Mobile quote cards; desktop table stays intact. */}
          <div className="agp-rem-mobile-list">
            {loading && <p className="agp-rem-empty">Loading quotes…</p>}
            {!loading && error && <p className="agp-rem-empty">{error}</p>}
            {!loading && !error && pageRows.length === 0 && <p className="agp-rem-empty">No quotes match your filters.</p>}
            {!loading && !error && pageRows.map((r) => {
              const info = statusInfo(r);
              return (
                <article key={r._id} className="agp-rem-card">
                  <button type="button" className="agp-rem-card-main" onClick={() => navigate(`/admin/requests/${r._id}`)}>
                    <div className="agp-rem-card-heading"><strong>{r.quoteId || r.requestId || "Quote"}</strong><span className={`agp-rem-status ${info.cls}`}>{info.label}</span></div>
                    <small>{r.requestId}</small>
                    <h3>{r.partRequired || r.notes || "Part not specified"}</h3>
                    <p>{r.customerName || "Customer"} · {r.rego || r.vin || "Vehicle not specified"}</p>
                    <div className="agp-rem-card-bottom"><span>Quote date<br/><strong>{fmtDate(r.quotedAt || r.updatedAt)}</strong></span><span>Quote price<br/><strong>{r.quotePricing?.total != null ? money(r.quotePricing.total) : "—"}</strong></span></div>
                  </button>
                  <div className="agp-rem-card-actions"><button type="button" onClick={() => navigate(`/admin/requests/${r._id}`)}>View quote</button><button type="button" onClick={() => navigate(`/admin/requests/${r._id}/quote`)}>Edit quote</button></div>
                </article>
              );
            })}
          </div>
          {/* table */}
          <div className="overflow-x-auto agp-rem-desktop-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#14123a] text-white text-xs uppercase tracking-wide">
                  <th className="text-left font-semibold px-6 py-4">Quote ID</th>
                  <th className="text-left font-semibold px-6 py-4">Customer</th>
                  <th className="text-left font-semibold px-6 py-4">Vehicle</th>
                  <th className="text-left font-semibold px-6 py-4">Part Required</th>
                  <th className="text-left font-semibold px-6 py-4">Quote Date</th>
                  <th className="text-left font-semibold px-6 py-4">Amount</th>
                  <th className="text-left font-semibold px-6 py-4">Status</th>
                  <th className="px-6 py-4" />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="text-center text-slate-400 py-12">Loading quotes…</td></tr>}
                {!loading && error && <tr><td colSpan={8} className="text-center text-red-500 py-12">{error}</td></tr>}
                {!loading && !error && pageRows.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-slate-400 py-12">
                    {quoted.length === 0 ? "No quotes yet. Open a request and create a quote." : "No quotes match your filters."}
                  </td></tr>
                )}
                {!loading && !error && pageRows.map((r) => {
                  const s = statusInfo(r);
                  return (
                    <tr key={r._id} onClick={() => navigate(`/admin/requests/${r._id}`)}
                      className={`border-b border-gray-100 last:border-0 hover:bg-slate-50 cursor-pointer ${r.quoteStatus === "revision_requested" ? "bg-purple-50/40" : ""}`}>
                      <td className="px-6 py-4">
                        <div className="font-bold text-blue-700">{r.quoteId}</div>
                        <div className="text-xs text-slate-400">{r.requestId}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-800">{r.customerName || "—"}</div>
                        <div className="text-xs text-slate-400">{r.customerContact || ""}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-500">
                        {r.rego ? `Rego ${r.rego}` : r.vin ? `VIN ${r.vin}` : r.vehicleImage ? "Photo" : "—"}
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-800">{r.partRequired || r.notes || "—"}</td>
                      <td className="px-6 py-4 text-slate-500">{fmtDate(r.quotedAt || r.updatedAt)}</td>
                      <td className="px-6 py-4 font-bold text-slate-800">
                        {r.quotePricing?.total != null ? money(r.quotePricing.total) : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-block text-[10px] font-bold px-2.5 py-1 rounded-full uppercase ${s.cls}`}>{s.label}</span>
                        {s.sub && <div className="text-[11px] text-slate-400 italic mt-1">{s.sub}</div>}
                      </td>
                      <td className="px-6 py-4 text-right relative" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setMenuOpen(menuOpen === r._id ? null : r._id)} aria-label="Quote actions"
                          className="text-slate-400 hover:text-slate-600">
                          <MoreVertical size={16} />
                        </button>
                        {menuOpen === r._id && (
                          <div ref={menuRef} className="absolute right-8 top-4 z-10 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-2 text-left">
                            <button onClick={() => navigate(`/admin/requests/${r._id}`)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                              <Eye size={15} /> View quote
                            </button>
                            <button onClick={() => navigate(`/admin/requests/${r._id}/quote`)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                              <Pencil size={15} /> Edit quote
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* pagination */}
          <div className="flex items-center justify-between px-6 py-4 flex-wrap gap-3">
            <span className="text-sm text-slate-400">
              {filtered.length === 0
                ? "No quotes"
                : `Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length} quotes`}
            </span>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} aria-label="Previous page"
                className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 text-slate-400 disabled:opacity-40">
                <ChevronLeft size={15} />
              </button>
              {pageWindow(page, totalPages).map((n) => (
                <button key={n} onClick={() => setPage(n)}
                  className={`w-8 h-8 grid place-items-center rounded-lg text-sm font-semibold ${n === page ? "bg-blue-700 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                  {n}
                </button>
              ))}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} aria-label="Next page"
                className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 text-slate-400 disabled:opacity-40">
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </main>
    </AdminLayout>
  );
}
